import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import BopLoaderOverlay from '../../components/BopLoaderOverlay';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { useFiltriApplicati } from '../../hooks/useFiltriApplicati';
import { BustaGo, StatoGo, dataIt, euro } from '../../lib/gestionaleOpen';
import { API_DOCS } from './RiconciliazioneGoPage.api';
import { BottoneCercaGo, InvitoCercaGo } from './CercaGo';

/**
 * Riconciliazione dei dati economici: perche' Vendita clienti / Costi
 * fornitori, Analisi commesse e Conto economico danno totali diversi.
 * Per ricavi e costi, due "ponti" che partono dal totale delle fatture e
 * arrivano al conto economico e all'analisi commesse, una differenza per
 * riga. Tutto per data di competenza, sullo stesso periodo.
 */

// Fatture di una voce (oggi solo il residuo fra fatture e registrazione): da correggere in GO
interface DocumentoPasso { numero: string | null; data: string | null; controparte: string | null; importo: number }
interface Passo { voce: string; codice?: string | null; importo: number; documenti?: DocumentoPasso[] }
interface Ponte { partenza: Passo; passi: Passo[]; arrivo: Passo; quadratura: number }
interface PontiLato { verso_conto_economico: Ponte; verso_commesse: Ponte; da_conto_economico_a_commesse: Ponte; integrati: number }
interface RispostaRiconciliazione {
  database: string;
  periodo: { anno: number; mese_da: number; mese_a: number; da: string; a: string; descrizione: string };
  ricavi: PontiLato;
  costi: PontiLato;
}

const ANNO_CORRENTE = new Date().getFullYear();
const MESE_CORRENTE = new Date().getMonth() + 1;
// Default: l'ultimo mese chiuso, come il conto economico
const ANNO_ULTIMO_CHIUSO = MESE_CORRENTE === 1 ? ANNO_CORRENTE - 1 : ANNO_CORRENTE;
const MESE_ULTIMO_CHIUSO = MESE_CORRENTE === 1 ? 12 : MESE_CORRENTE - 1;
const ANNI = Array.from({ length: 6 }, (_, i) => ANNO_CORRENTE - i);
const NOMI_MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

function TabellaPonte({ titolo, ponte }: { titolo: string; ponte: Ponte }) {
  return (
    <div className="bop-card bop-riconciliazione__ponte">
      <div className="bop-card__header">
        <h3 className="bop-card__title">{titolo}</h3>
      </div>
      <table className="bop-table bop-riconciliazione__tabella">
        <tbody>
          <tr className="bop-riconciliazione__riga bop-riconciliazione__riga--estremo">
            <td className="bop-riconciliazione__voce">{ponte.partenza.voce}</td>
            <td className="bop-riconciliazione__importo">{euro(ponte.partenza.importo)}</td>
          </tr>
          {ponte.passi.map((p, i) => (
            <tr key={`${i}-${p.voce}`} className="bop-riconciliazione__riga">
              <td className="bop-riconciliazione__voce bop-riconciliazione__voce--passo">
                {p.voce}
                {p.documenti && p.documenti.length > 0 && (
                  <ul className="bop-riconciliazione__documenti">
                    {p.documenti.map((d, j) => (
                      <li key={`${j}-${d.numero}`} className="bop-riconciliazione__documento">
                        n. {d.numero ?? '?'} del {dataIt(d.data)} · {d.controparte ?? 'senza controparte'} · {euro(d.importo)}
                      </li>
                    ))}
                  </ul>
                )}
              </td>
              <td className={`bop-riconciliazione__importo${p.importo < 0 ? ' bop-text-danger' : ''}`}>
                {p.importo > 0 ? '+' : ''}{euro(p.importo)}
              </td>
            </tr>
          ))}
          <tr className="bop-riconciliazione__riga bop-riconciliazione__riga--estremo bop-riconciliazione__riga--arrivo">
            <td className="bop-riconciliazione__voce">= {ponte.arrivo.voce}</td>
            <td className="bop-riconciliazione__importo">{euro(ponte.arrivo.importo)}</td>
          </tr>
        </tbody>
      </table>
      {ponte.quadratura !== 0 && (
        <div className="bop-alert bop-alert--warning">
          Differenza non spiegata dai passi: {euro(ponte.quadratura)}. Segnalala: i dati di GO hanno un caso nuovo.
        </div>
      )}
    </div>
  );
}

export default function RiconciliazioneGoPage() {
  const { societaId: societaSel, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [annoSel, setAnno] = useState<number>(ANNO_ULTIMO_CHIUSO);
  const [meseDaSel, setMeseDa] = useState<number>(1);
  const [meseASel, setMeseA] = useState<number>(MESE_ULTIMO_CHIUSO);
  const { applicati, versione, applica, modificati, cercato } = useFiltriApplicati(
    { societaId: societaSel, anno: annoSel, meseDa: meseDaSel, meseA: meseASel }, societaSel,
  );
  const { societaId, anno, meseDa, meseA } = applicati;

  const [dati, setDati] = useState<RispostaRiconciliazione | null>(null);
  const [stato, setStato] = useState<StatoGo>('OK');
  const [avviso, setAvviso] = useState('');
  const [errore, setErrore] = useState('');
  const [caricando, setCaricando] = useState(false);

  const cambiaAnno = (a: number) => {
    setAnno(a);
    setMeseA(a === ANNO_CORRENTE ? Math.max(MESE_ULTIMO_CHIUSO, meseDaSel) : 12);
  };

  useEffect(() => {
    if (!cercato || !societaId) { setDati(null); setStato('OK'); return undefined; }
    let annullato = false;
    (async () => {
      setCaricando(true);
      setErrore('');
      setAvviso('');
      try {
        const p = new URLSearchParams({ societaId, anno: String(anno), mese_da: String(meseDa), mese_a: String(meseA) });
        // @api GET /api/bop-go/riconciliazione?societaId=1&anno=2026&mese_da=1&mese_a=8 -> {"success":true,"stato":"OK","data":{"database":"arc_solu","periodo":{"descrizione":"gen–ago 2026"},"ricavi":{"verso_conto_economico":{"partenza":{"voce":"Vendita clienti (fatture di vendita)","importo":2205408.12},"passi":[{"voce":"Quote di competenza del periodo","importo":103174.31}],"arrivo":{"voce":"Conto economico (ricavi, con competenze)","importo":2429204.89},"quadratura":0},"verso_commesse":{},"integrati":146192.16},"costi":{}}}
        const res = await apiFetch<BustaGo<RispostaRiconciliazione>>(`/api/bop-go/riconciliazione?${p.toString()}`);
        if (annullato) return;
        setStato(res.stato);
        setDati(res.data);
        if (res.stato !== 'OK' && res.error) setAvviso(res.error);
      } catch (err) {
        if (annullato) return;
        setErrore(err instanceof Error ? err.message : 'Errore nella riconciliazione dei dati da GO');
        setDati(null);
      } finally {
        if (!annullato) setCaricando(false);
      }
    })();
    return () => { annullato = true; };
  }, [cercato, societaId, anno, meseDa, meseA, versione]);

  return (
    <Layout title="Riconciliazione dati economici">
      <div className="bop-page-header">
        <h1 className="bop-page-title">Riconciliazione dati economici</h1>
      </div>

      <p className="bop-text-muted">
        Perché Vendita clienti, Costi fornitori, Analisi commesse e Conto economico danno totali
        diversi. Ogni pagina misura una cosa diversa: le fatture, i movimenti di commessa, la
        contabilità generale con competenze. Qui si parte dal totale delle fatture e si arriva agli
        altri due, una differenza per riga. Tutto per data di competenza.
      </p>

      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select" value={societaSel} onChange={(e) => setSocietaId(e.target.value)} aria-label="Società">
            <option value="">Seleziona società</option>
            {listaSocieta.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <select className="bop-select" value={annoSel} onChange={(e) => cambiaAnno(Number(e.target.value))} aria-label="Anno">
          {ANNI.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="bop-select" value={meseDaSel} onChange={(e) => { const v = Number(e.target.value); setMeseDa(v); if (v > meseASel) setMeseA(v); }} aria-label="Da mese">
          {NOMI_MESI.map((m, i) => <option key={m} value={i + 1}>da {m.toLowerCase()}</option>)}
        </select>
        <select className="bop-select" value={meseASel} onChange={(e) => setMeseA(Number(e.target.value))} aria-label="A mese">
          {NOMI_MESI.map((m, i) => <option key={m} value={i + 1} disabled={i + 1 < meseDaSel}>a {m.toLowerCase()}</option>)}
        </select>
        <BottoneCercaGo
          cercato={cercato}
          modificati={modificati}
          disabled={!societaSel || caricando}
          onClick={applica}
        />
      </div>

      <BopLoaderOverlay visibile={caricando} testo="Lettura di fatture, commesse e contabilità da Gestionale Open…" />

      {!societaId && <div className="bop-empty-state">Seleziona una società.</div>}
      {societaId && !cercato && <InvitoCercaGo />}
      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}
      {societaId && stato === 'NON_MAPPATA' && (
        <div className="bop-empty-state">
          Questa società non ha un database di Gestionale Open associato. Si imposta in
          {' '}<strong>Impostazioni Generali → Società del gruppo</strong>, campo «Database Gestionale Open».
        </div>
      )}
      {societaId && stato === 'NON_DISPONIBILE' && (
        <div className="bop-empty-state">
          <div className="bop-empty-state__title">Dati non ancora disponibili per questa società</div>
          <div className="bop-empty-state__text">{avviso}</div>
        </div>
      )}

      {societaId && stato === 'OK' && dati && (
        <>
          <h2 className="bop-riconciliazione__sezione">Ricavi — {dati.periodo.descrizione}</h2>
          <div className="bop-riconciliazione__griglia">
            <TabellaPonte titolo="Da Vendita clienti al Conto economico" ponte={dati.ricavi.verso_conto_economico} />
            <TabellaPonte titolo="Da Vendita clienti all'Analisi commesse" ponte={dati.ricavi.verso_commesse} />
            <TabellaPonte titolo="Dal Conto economico all'Analisi commesse" ponte={dati.ricavi.da_conto_economico_a_commesse} />
          </div>

          <h2 className="bop-riconciliazione__sezione">Costi — {dati.periodo.descrizione}</h2>
          <div className="bop-riconciliazione__griglia">
            <TabellaPonte titolo="Da Costi fornitori al Conto economico" ponte={dati.costi.verso_conto_economico} />
            <TabellaPonte titolo="Da Costi fornitori all'Analisi commesse" ponte={dati.costi.verso_commesse} />
            <TabellaPonte titolo="Dal Conto economico all'Analisi commesse" ponte={dati.costi.da_conto_economico_a_commesse} />
          </div>

          <p className="bop-analisi-go__nota">
            Database <strong>{dati.database}</strong> · periodo per data di competenza · il conto economico
            è quello «con competenze» (le quote dei periodi di competenza ripartite per giorni) · l'analisi
            commesse comprende le fatture integrate da BOP
            {(dati.ricavi.integrati !== 0 || dati.costi.integrati !== 0) && (
              <> ({euro(dati.ricavi.integrati)} di ricavi{dati.costi.integrati !== 0 && <>, {euro(dati.costi.integrati)} di costi</>}): commessa sulla riga, nessun movimento in GO</>
            )}.
            Le causali di prima nota sotto i 500 € sono raccolte in una riga. Sotto le «altre differenze»
            fra fatture e registrazione sono elencate le fatture (le 20 più grandi) la cui prima nota non
            corrisponde alla fattura: sono da controllare in Gestionale Open.
          </p>
        </>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
